/**
 * Run an async mapper over `items` with at most `limit` calls in flight.
 *
 * `Promise.all(items.map(fn))` dispatches everything at once — fine for a
 * handful, wrong for a per-row fan-out, where a single collection page can put
 * enough simultaneous reads on record-service to make it answer 500/503 or fall
 * over. Capping the dispatch keeps the same total work and bounds the peak.
 *
 * It also bounds cancellation: work that has not started yet is never
 * dispatched, where an aborted `Promise.all` only stops the JS-side promises
 * after every request has already left the browser.
 *
 * Order is preserved: results land at the index of their input regardless of
 * which finished first.
 */
export const mapWithConcurrency = async <TItem, TResult>(
  items: readonly TItem[],
  limit: number,
  fn: (item: TItem, index: number) => Promise<TResult>,
  signal?: AbortSignal,
): Promise<TResult[]> => {
  if (items.length === 0) {
    return [];
  }

  const workers = Math.max(1, Math.min(limit, items.length));
  const results = new Array<TResult>(items.length);
  let cursor = 0;

  const runWorker = async (): Promise<void> => {
    while (cursor < items.length) {
      // `signal.throwIfAborted()` rather than a silent break: a partial result
      // array would look like "the parent has fewer children" to every caller.
      signal?.throwIfAborted();

      const index = cursor;
      cursor += 1;
      results[index] = await fn(items[index], index);
    }
  };

  await Promise.all(Array.from({ length: workers }, runWorker));

  return results;
};

/**
 * Per-row record reads (dereference, child lookups).
 *
 * Six matches what browsers historically allowed per HTTP/1.1 origin, and keeps
 * a page-sized fan-out latency-bound rather than serialised. Raising it buys
 * little and is what pushes the backend over.
 */
export const RECORD_FETCH_CONCURRENCY = 6;

/**
 * A gate that lets at most `limit` holders through at a time.
 *
 * Needed because {@link mapWithConcurrency} only bounds a fan-out the caller
 * owns. Per-row `useQueries` (child traversals, leftover dereference) dispatch
 * every query immediately and offer no concurrency option.
 *
 * Putting the gate inside the shared query function instead bounds every caller
 * at once — the per-row join, the child traversals, and anything added later —
 * without each having to remember. Safe against deadlock only because nothing
 * held inside the gate waits on something else that needs it; keep it that way.
 */
export const createSemaphore = (limit: number) => {
  let active = 0;
  const waiting: Array<() => void> = [];

  const release = (): void => {
    active -= 1;
    waiting.shift()?.();
  };

  return async <T>(task: () => Promise<T>): Promise<T> => {
    if (active >= limit) {
      await new Promise<void>((resolve) => waiting.push(resolve));
    }

    active += 1;

    try {
      return await task();
    } finally {
      release();
    }
  };
};

/**
 * Shared gate for per-record reads. One instance so the limit is global rather
 * than per call site — six fan-outs of six is still thirty-six in flight.
 */
export const withRecordFetchSlot = createSemaphore(RECORD_FETCH_CONCURRENCY);
